'use client'

import { twMerge } from '@/lib/classname/twMerge'
import { cva } from 'class-variance-authority'
import type { TableProps as AriaTableProps } from 'react-aria-components'
import { Table as AriaTable } from 'react-aria-components'
import { Cell } from './_components/Cell.client'
import { Column } from './_components/Column.client'
import { Empty } from './_components/Empty.server'
import { NoResult } from './_components/NoResult.server'
import { Row } from './_components/Row.client'
import { TableBody } from './_components/TableBody.client'
import { TableHeader } from './_components/TableHeader.client'
import { TableContext, type TableContextType } from './TableContext'

type TableProps = AriaTableProps & {
  selectionBehavior?: 'toggle' | 'replace'
  selectionMode?: 'single' | 'multiple'
  responsive?: boolean
  layout?: 'fixed' | 'auto'
} & TableContextType & { 'aria-label': string; 'data-testid'?: string; id?: string }

export const tableStyles = cva('border-px border-brand-2-200', {
  variants: {
    rounded: {
      true: 'overflow-hidden rounded-sm',
    },
  },
})

/**
 * Table is a component that displays data in a tabular format. It is built on top of the react-aria Table component and provides additional features such as responsive design, selection behavior, and custom styling.
 *
 * Example usage:
 *
 * ```tsx
 * import { Table } from '@/ui/tables/table/Table.client'
 *
 * const data = [
 *  { id: '1', name: 'John Doe', age: 30 },
 *  { id: '2', name: 'Jane Smith', age: 25 },
 * ]
 *
 * <Table aria-label="Example Table" rounded separators>
 *   <Table.Header>
 *     <Table.Column id="name" />
 *     <Table.Column id="age" allowsSorting />
 *   </Table.Header>
 *   <Table.Body items={data}>
 *     {(item) => (
 *       <Table.Row key={item.id}>
 *         <Table.Cell>{item.name}</Table.Cell>
 *         <Table.Cell>{item.age}</Table.Cell>
 *       </Table.Row>
 *     )}
 *   </Table.Body>
 * </Table>
 * ```
 *
 * @param className - Additional class names to apply to the table container.
 * @param variant - The variant of the table: 'light', 'primary', or 'dark'. Defaults to 'light'.
 * @param rounded - Whether the table should have rounded corners.
 * @param separators - Whether the table should have separators between rows.
 * @param layout - The layout of the table, either 'fixed' or 'auto'. Defaults to 'auto'.
 * @returns The rendered Table component.
 */
function Table({
  className,
  variant = 'light',
  rounded = false,
  separators = false,
  layout = 'auto',
  id,
  'data-testid': dataTestId,
  ...props
}: TableProps) {
  return (
    <TableContext.Provider value={{ variant, rounded, separators }}>
      <div
        id={id}
        data-testid={dataTestId}
        className={twMerge(className, tableStyles({ rounded }))}
      >
        <AriaTable
          {...props}
          className={twMerge(
            'w-full min-w-full table-auto border-collapse text-brand-2-900 has-[>[data-empty]]:h-full',
            layout === 'fixed' && 'table-fixed',
          )}
        />
      </div>
    </TableContext.Provider>
  )
}

Table.Header = TableHeader
Table.Body = TableBody
Table.Column = Column
Table.Row = Row
Table.Cell = Cell
Table.Empty = Empty
// Distinct from Empty: shown when filters exclude every row, not when the table has no data
Table.NoResult = NoResult

export { Table }
